// Day targets: the pure logic of the fixed-values editor and of the prediction display.
// A day target is one value for each player and metric. Position rows and the team row are
// computed here, never stored. The backend rules live in api/Util/DayTargetUtil.py.

export const TOTAL_TIME_KEY = "total_time";
export const MAX_VALUE = 99999.999;

// Peak metrics: the editor's team and position rows take the max for these, the mean for the rest.
export const PEAK_METRICS = ["top_speed", "top_speed_percent", "accel_max", "decel_max", "max_hr"];

export const CALCULATOR_SOURCES = ["weekday_data", "weekday_targets", "hist_avg", "baseline", "all_time_best"];

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

export const roundValue = (value, decimals) => {
	if (!isNumber(value)) return null;
	const factor = 10 ** Math.min(decimals ?? 0, 3);
	return Math.round(value * factor) / factor;
};

export const isValidValue = (value) => isNumber(value) && value >= 0 && value <= MAX_VALUE;

/** The largest value a column can hold after rounding to its decimals: 99999 for 0 decimals. */
export const maxValueFor = (decimals) => {
	const factor = 10 ** Math.min(decimals ?? 0, 3);
	return Math.floor(MAX_VALUE * factor + 1e-6) / factor;
};

/** The editor's aggregation across players: max for peak metrics, mean for the rest. */
export const aggregate = (values, metricKey) => {
	const present = values.filter(isNumber);
	if (!present.length) return null;
	if (PEAK_METRICS.includes(metricKey)) return Math.max(...present);
	return present.reduce((sum, value) => sum + value, 0) / present.length;
};

/** The calculator labels, with the short weekday of the day: "Avg of last 4 Sat (data)". */
export const sourceLabels = (weekday) => ({
	weekday_data: `Avg of last 4 ${weekday} (data)`,
	weekday_targets: `Avg of last 4 ${weekday} (targets)`,
	hist_avg: "Hist Avg",
	baseline: "Baseline",
	all_time_best: "All time Best",
});

/** Total Time first, then the visible GPS metrics in their assigned order. */
export const editorColumns = (teamMetrics) => [
	{ key: TOTAL_TIME_KEY, name: "Total Time", decimals: 0 },
	...(teamMetrics ?? [])
		.filter((metric) => metric?.key && metric.visible !== false)
		.map((metric) => ({ key: metric.key, name: metric.name, decimals: metric.decimal_places ?? 0 })),
];

/** Active players grouped by position: [{ positionId, name, players: [{ id, name }] }], sorted by name. */
export const groupByPosition = (players) => {
	const groups = new Map();
	(players ?? []).forEach((player) => {
		if (!player || player.retired) return;
		const positionId = player.position_id ?? null;
		if (!groups.has(positionId)) {
			groups.set(positionId, { positionId, name: player.position || "No position", players: [] });
		}
		groups.get(positionId).players.push({
			id: String(player.player_id),
			name: `${player.first_name ?? ""} ${player.last_name ?? ""}`.trim(),
		});
	});
	const byName = (a, b) => a.name.localeCompare(b.name);
	return [...groups.values()]
		.map((group) => ({ ...group, players: [...group.players].sort(byName) }))
		.sort(byName);
};

/** The value of a row over its players: { [metricKey]: number | null }. */
export const rowValues = (playerIds, values, columns) =>
	Object.fromEntries(columns.map(({ key, decimals }) => [
		key,
		roundValue(aggregate(playerIds.map((id) => values?.[id]?.[key]), key), decimals),
	]));

/** A position-row edit overwrites the value of every player in the position. */
export const applyPositionEdit = (values, playerIds, metricKey, value) => {
	const next = { ...values };
	playerIds.forEach((id) => {
		next[id] = { ...(next[id] ?? {}), [metricKey]: value };
	});
	return next;
};

/**
 * True when a row shows a value other than its value in the snapshot (the last load or apply). A
 * position row compares its shown value, so a value typed back to the original is no change.
 */
export const rowChanged = (values, snapshot, playerIds, column) =>
	rowValues(playerIds, values, [column])[column.key] !== rowValues(playerIds, snapshot, [column])[column.key];

/**
 * The fallback marks after an edit: an edited cell holds the coach's own value, so it loses its mark;
 * a cell back at its snapshot value gets its snapshot mark back.
 */
export const marksAfterEdit = (marks, snapshotMarks, values, snapshot, playerIds, metricKey, decimals) => {
	const next = { ...marks };
	playerIds.forEach((id) => {
		const unchanged = roundValue(values[id]?.[metricKey], decimals) === roundValue(snapshot?.[id]?.[metricKey], decimals);
		next[id] = { ...(next[id] ?? {}), [metricKey]: unchanged ? (snapshotMarks?.[id]?.[metricKey] ?? "") : "" };
	});
	return next;
};

/** A blank cell (no number) of the players for a metric. 0 is a value; a blank is not. */
export const hasBlankCell = (values, playerIds, metricKey) => playerIds.some((id) => !isNumber(values?.[id]?.[metricKey]));

export const applyCellEdit = (values, playerId, metricKey, value) => ({
	...values,
	[playerId]: { ...(values?.[playerId] ?? {}), [metricKey]: value },
});

/**
 * "Apply to table": each player gets their own reference value × percent, rounded and capped at
 * the column maximum, so the table never shows a value that Save would drop. A cell with no reference value
 * (for example Total Time from Hist Avg) keeps its current value.
 */
export const applyCalculator = (values, referenceValues, percent, columns, playerIds) => {
	const next = { ...values };
	playerIds.forEach((id) => {
		const reference = referenceValues?.[id] ?? {};
		const row = { ...(next[id] ?? {}) };
		columns.forEach(({ key, decimals }) => {
			if (isNumber(reference[key])) row[key] = Math.min(roundValue((reference[key] * percent) / 100, decimals), maxValueFor(decimals));
		});
		next[id] = row;
	});
	return next;
};

/** "Clear targets": every cell becomes 0. Nothing is saved until "Save targets". */
export const clearValues = (playerIds, columns) =>
	Object.fromEntries(playerIds.map((id) => [id, Object.fromEntries(columns.map(({ key }) => [key, 0]))]));

/**
 * The server's message for a request it refused (a 4xx answer, for example a 409 conflict), else the
 * fallback. A 5xx body can be an HTML error page, so it never shows.
 */
export const errorText = (e, fallback) => {
	const { status, data } = e?.response ?? {};
	return status >= 400 && status < 500 && typeof data === "string" && data ? data : fallback;
};

/** True when any cell differs from the snapshot after rounding. */
export const valuesDiffer = (values, snapshot, playerIds, columns) =>
	playerIds.some((id) => columns.some(({ key, decimals }) =>
		roundValue(values?.[id]?.[key], decimals) !== roundValue(snapshot?.[id]?.[key], decimals)));

/** "Apply to table" turns on when the calculator or any table value differs from the last apply. */
export const canApply = ({ source, percent, applied, values, snapshot, playerIds, columns }) =>
	source !== applied.source || percent !== applied.percent || valuesDiffer(values, snapshot, playerIds, columns);

/** The save payload: only cells that hold a valid number. */
export const toPayload = (values, playerIds, columns) => {
	const payload = {};
	playerIds.forEach((id) => {
		const row = {};
		columns.forEach(({ key, decimals }) => {
			const value = roundValue(values?.[id]?.[key], decimals);
			if (isValidValue(value)) row[key] = value;
		});
		if (Object.keys(row).length) payload[id] = row;
	});
	return payload;
};

/**
 * The team row uses only the players with a value of their own, as its popover does: a cell that
 * `marks` names holds a fallback value. With no such player, it uses every player, so it shows the
 * fallback the cells show.
 */
const teamValues = (playerIds, values, columns, marks) =>
	Object.fromEntries(columns.map((column) => {
		const own = playerIds.filter((id) => !marks?.[id]?.[column.key]);
		return [column.key, rowValues(own.length ? own : playerIds, values, [column])[column.key]];
	}));

/**
 * The table rows of the editor: the team row, then each position with
 * its players as children. A position row shows the aggregate of its players.
 */
export const buildTargetRows = (positions, values, columns, marks = {}) => {
	const allIds = positions.flatMap((position) => position.players.map((player) => player.id));
	return [
		{ key: "team", kind: "team", name: "Team Avg*", players: allIds, values: teamValues(allIds, values, columns, marks) },
		...positions.map((position) => {
			const ids = position.players.map((player) => player.id);
			return {
				key: `position-${position.positionId}`,
				kind: "position",
				positionId: position.positionId,
				name: `${position.name} (${ids.length})`,
				players: ids,
				values: rowValues(ids, values, columns),
				children: position.players.map((player) => ({
					key: `player-${player.id}`, kind: "player", id: player.id, name: player.name,
					players: [player.id], values: rowValues([player.id], values, columns),
				})),
			};
		}),
	];
};
